import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  type DocumentDetail,
  DocumentSubType,
  type FormInt,
  type Interrogatory,
} from "@/briefpoint-client";
import Fallback from "@/components/Fallback";
import Loading from "@/components/Loading";
import { toast } from "@/components/ui/toast";
import { useDocumentApi } from "@/hooks/useApi";
import { useDocumentFormInterrogatories } from "@/hooks/useDocumentDetail";
import { getDocumentOptions } from "@/lib/query-options";
import InterrogatoriesList from "./InterrogatoriesList";
import NextButton from "./NextButton";
import ResponsesNavigator from "./ResponsesNavigator";
import SelectAllBar from "./SelectAllBar";
import SupplementalResponseLayout from "./SupplementalResponseLayout";

export default function SupplementalResponse() {
  const history = useHistory();
  const documentApi = useDocumentApi();

  const { documentId } = useParams<{ documentId: string }>();
  const [selectedInterrogatories, setSelectedInterrogatories] = useState<
    Set<number>
  >(new Set());

  // Fetch the current document
  const documentQuery = useQuery({
    ...getDocumentOptions(documentId ?? "", documentApi),
    enabled: !!documentId,
  });

  const document = documentQuery.data;

  // If this is a supplemental document, fetch the parent document for interrogatories
  const parentDocumentId = document?.parentDocumentId;
  const isSupplemental = document?.info?.isSupplementalDocument;

  const parentDocumentQuery = useQuery({
    ...getDocumentOptions(parentDocumentId ?? "", documentApi),
    enabled: !!parentDocumentId && !!isSupplemental,
  });

  // Get interrogatories from the current document
  // When supplementing a supplemental, we only want the interrogatories that were selected for it
  const interrogatories: Interrogatory[] = useMemo(() => {
    return document?.interrogatories ?? [];
  }, [document]);

  const documentStartingNumber = useMemo(() => {
    if (isSupplemental && parentDocumentQuery.data) {
      return parentDocumentQuery.data.info?.startingNumber ?? 1;
    }
    return document?.info?.startingNumber ?? 1;
  }, [document, isSupplemental, parentDocumentQuery.data]);

  const documentName = useMemo(() => {
    if (isSupplemental && parentDocumentQuery.data) {
      return parentDocumentQuery.data.name ?? "";
    }
    return document?.name ?? "";
  }, [document, isSupplemental, parentDocumentQuery.data]);

  const documentSubType = useMemo(() => {
    if (isSupplemental && parentDocumentQuery.data) {
      return parentDocumentQuery.data.info?.documentSubType;
    }
    return document?.info?.documentSubType;
  }, [document, isSupplemental, parentDocumentQuery.data]);

  // Fetch form interrogatories if this is a FROGS document
  const formInterrogatories = useDocumentFormInterrogatories(documentSubType);

  // Create a map from FormInt id to FormInt for easy lookup
  const formIntsMap = useMemo(() => {
    if (!formInterrogatories) return null;
    const map = new Map<string, FormInt>();
    for (const formInt of formInterrogatories) {
      if (formInt.id) {
        map.set(formInt.id, formInt);
      }
    }
    return map;
  }, [formInterrogatories]);

  const isFormInterrogatories =
    documentSubType === DocumentSubType.FormInterrogatorries;

  const createSupplementalMutation = useMutation({
    mutationFn: async (interrogatoryIds: number[]) => {
      return await documentApi.documentCreateSupplementalResponse({
        documentId,
        createSupplementalResponseRequest: {
          interrogatoriesToSupplement: interrogatoryIds,
        },
      });
    },
    onSuccess: (data: DocumentDetail) => {
      const newDocumentId = data?.id;
      history.push(`/wizard/${newDocumentId}/0`);
    },
    onError: () => {
      toast.error(
        "Failed to create supplemental response. Please try again later.",
      );
    },
  });

  const toggleSelect = useCallback(
    (id: number | null) => {
      let newSet: Set<number>;

      if (id === null) {
        // Toggle all
        if (selectedInterrogatories.size === interrogatories.length) {
          newSet = new Set();
        } else {
          newSet = new Set(interrogatories.map((i) => i.id));
        }
      } else {
        // Toggle one
        newSet = new Set(selectedInterrogatories);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
      }

      setSelectedInterrogatories(newSet);
    },
    [interrogatories, selectedInterrogatories],
  );

  const handleClickNext = useCallback(() => {
    if (
      selectedInterrogatories.size === 0 ||
      createSupplementalMutation.isPending
    ) {
      return;
    }

    // Map selected IDs to parent interrogatory IDs for the API
    // When supplementing a supplemental, the API expects the parent's interrogatory IDs
    const interrogatoryIds = interrogatories
      .filter((i) => selectedInterrogatories.has(i.id))
      .map((i) => i.parentInterrogatoryId ?? i.id);

    createSupplementalMutation.mutate(interrogatoryIds);
  }, [selectedInterrogatories, createSupplementalMutation, interrogatories]);

  const isLoading =
    documentQuery.isLoading ||
    !!(isSupplemental && parentDocumentQuery.isLoading) ||
    (isFormInterrogatories && !formIntsMap);
  const isPending = createSupplementalMutation.isPending;

  const isError = documentQuery.isError || parentDocumentQuery.isError;
  if (isError) {
    return (
      <Fallback error={documentQuery.error ?? parentDocumentQuery.error} />
    );
  }

  return (
    <Loading className="mt-3" isLoading={isLoading}>
      <SupplementalResponseLayout
        sidebar={
          <ResponsesNavigator
            interrogatories={interrogatories}
            selectedInterrogatories={selectedInterrogatories}
            documentStartingNumber={documentStartingNumber}
            formIntsMap={formIntsMap}
          />
        }
        documentTitle={documentName}
        footer={
          <NextButton
            onClick={handleClickNext}
            disabled={isPending || selectedInterrogatories.size === 0}
            isPending={isPending}
          />
        }
      >
        <SelectAllBar
          allSelected={selectedInterrogatories.size === interrogatories.length}
          disabled={isPending}
          onToggleAll={() => toggleSelect(null)}
        />
        <InterrogatoriesList
          interrogatories={interrogatories}
          selectedInterrogatories={selectedInterrogatories}
          documentStartingNumber={documentStartingNumber}
          disabled={isPending}
          onToggleSelect={toggleSelect}
          formIntsMap={formIntsMap}
        />
      </SupplementalResponseLayout>
    </Loading>
  );
}
